<!-- src/views/Restaurante/ModificadoresList.vue -->
<template>
  <div class="p-3 sm:p-6 text-lexend overflow-x-hidden min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-900/80">

    <!-- HEADER -->
    <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
      <div class="animate-fadeIn w-full sm:w-auto">
        <div class="flex items-center gap-2 mb-1.5">
          <div class="w-10 h-1.5 bg-gradient-to-r from-orange-500 to-red-500 rounded-full"></div>
          <span class="text-[10px] sm:text-xs font-black text-orange-600 dark:text-orange-400 tracking-widest uppercase">Módulo Restaurante</span>
        </div>
        <h1 class="text-2xl sm:text-4xl font-black text-gray-900 dark:text-white tracking-tight flex items-center flex-wrap gap-3">
          Modificadores
          <span class="px-3 py-1 bg-white dark:bg-gray-800 text-orange-600 dark:text-orange-400 text-xs sm:text-sm font-black rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm">
            {{ modificadores.length }}
          </span>
        </h1>
        <p class="text-sm text-gray-500 dark:text-gray-400 mt-1 font-medium">Opciones y extras para los productos del menú</p>
      </div>

      <button @click="openCreateModal"
        class="w-full sm:w-auto px-4 sm:px-6 py-2.5 bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 text-white rounded-xl font-black text-xs sm:text-sm tracking-wide transition-all shadow-lg shadow-orange-500/30 flex items-center justify-center gap-2">
        <Icon icon="mdi:plus-circle" class="text-lg" />
        <span>Nuevo Modificador</span>
      </button>
    </div>

    <!-- FILTROS -->
    <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-4 mb-6 animate-fadeInUp">
      <div class="flex flex-col sm:flex-row gap-4 items-end">
        <div class="flex-1 w-full">
          <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2 ml-1 uppercase">Buscar</label>
          <div class="relative">
            <Icon icon="mdi:magnify" class="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-xl" />
            <input v-model="searchQuery" type="text" placeholder="Buscar por nombre..."
              class="w-full pl-12 pr-4 py-3 bg-gray-50 dark:bg-gray-900/50 border-2 border-transparent focus:border-orange-500 rounded-xl transition-all outline-none dark:text-white font-bold text-sm" />
          </div>
        </div>
        <div class="w-full sm:w-44">
          <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2 ml-1 uppercase">Estado</label>
          <select v-model="filterStatus"
            class="w-full px-4 py-3 bg-gray-50 dark:bg-gray-900/50 border-2 border-transparent focus:border-orange-500 rounded-xl font-bold text-gray-700 dark:text-gray-200 outline-none text-sm">
            <option value="all">Todos</option>
            <option value="activos">Activos</option>
            <option value="inactivos">Inactivos</option>
          </select>
        </div>
        <button @click="cargarModificadores"
          class="w-full sm:w-auto px-5 py-3 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-xl font-black text-xs hover:bg-gray-200 transition-all flex items-center justify-center gap-2">
          <Icon icon="mdi:refresh" :class="{'animate-spin': loading}" class="text-lg" />
          <span class="hidden sm:inline">Actualizar</span>
        </button>
      </div>
    </div>

    <!-- CONTENIDO -->
    <div class="animate-fadeInUp delay-100">
      <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        <div v-if="loading" v-for="i in 3" :key="'load-'+i"
          class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-5 animate-pulse h-52"></div>

        <div v-if="!loading && modificadoresFiltrados.length === 0"
          class="col-span-full bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 p-10 text-center">
          <Icon icon="mdi:tune-variant" class="text-6xl mb-3 text-gray-300 mx-auto" />
          <h4 class="text-lg font-black text-gray-400 uppercase">No hay modificadores</h4>
          <p class="text-sm text-gray-400 mt-1">Crea tu primer modificador para empezar</p>
        </div>

        <div v-for="mod in modificadoresFiltrados" :key="mod.idModificador"
          class="bg-white dark:bg-gray-800 rounded-2xl shadow-md border border-gray-100 dark:border-gray-700 overflow-hidden hover:shadow-xl transition-all flex flex-col group">
          <div class="p-5 flex-1">
            <div class="flex items-start justify-between mb-3">
              <div class="flex items-center gap-3">
                <div :class="['w-12 h-12 rounded-2xl flex items-center justify-center shadow-sm border',
                  mod.estado === 'activo'
                    ? 'bg-gradient-to-br from-orange-50 to-red-50 border-orange-100 dark:from-orange-900/40 dark:to-red-900/40 dark:border-orange-800/50'
                    : 'bg-gray-50 border-gray-100 dark:bg-gray-800 dark:border-gray-700']">
                  <Icon icon="mdi:tune-variant"
                    :class="['text-2xl', mod.estado === 'activo' ? 'text-orange-600 dark:text-orange-400' : 'text-gray-400']" />
                </div>
                <div>
                  <h3 class="font-black text-gray-900 dark:text-white text-base">{{ mod.nombre }}</h3>
                  <p class="text-xs font-bold text-gray-400 mt-0.5">ID: {{ mod.idModificador }}</p>
                </div>
              </div>
              <span :class="['px-2.5 py-1 text-[10px] font-black uppercase tracking-widest rounded-lg border',
                mod.estado === 'activo'
                  ? 'bg-emerald-50 text-emerald-700 border-emerald-100 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800/50'
                  : 'bg-rose-50 text-rose-700 border-rose-100 dark:bg-rose-900/30 dark:text-rose-300 dark:border-rose-800/50']">
                {{ convertirEstadoATexto(mod.estado) }}
              </span>
            </div>

            <div class="flex flex-wrap gap-1.5 mb-3">
              <span class="px-2 py-0.5 bg-purple-50 text-purple-700 rounded-md text-[10px] font-black uppercase">
                {{ mod.tipo }}
              </span>
              <span v-if="mod.es_obligatorio" class="px-2 py-0.5 bg-red-50 text-red-700 rounded-md text-[10px] font-black uppercase">
                Obligatorio
              </span>
              <span v-if="mod.seleccion_multiple" class="px-2 py-0.5 bg-blue-50 text-blue-700 rounded-md text-[10px] font-black uppercase">
                Múltiple (max {{ mod.max_selecciones }})
              </span>
              <span v-else class="px-2 py-0.5 bg-gray-100 text-gray-600 rounded-md text-[10px] font-black uppercase">
                Única
              </span>
            </div>

            <div class="grid grid-cols-2 gap-2">
              <div class="bg-gray-50 dark:bg-gray-900/40 rounded-xl p-2 border border-gray-100 dark:border-gray-700/50">
                <p class="text-[9px] font-black text-gray-400 uppercase tracking-widest">Opciones</p>
                <p class="text-lg font-black text-orange-600">{{ mod.TotalOpciones || 0 }}</p>
              </div>
              <div class="bg-gray-50 dark:bg-gray-900/40 rounded-xl p-2 border border-gray-100 dark:border-gray-700/50">
                <p class="text-[9px] font-black text-gray-400 uppercase tracking-widest">Productos</p>
                <p class="text-lg font-black text-blue-600">{{ mod.TotalProductos || 0 }}</p>
              </div>
            </div>
          </div>

          <div class="bg-gray-50/50 dark:bg-gray-900/30 border-t border-gray-100 dark:border-gray-700 p-3 grid grid-cols-3 gap-2">
            <button @click.stop="irADetalle(mod)"
              class="col-span-3 py-2.5 bg-gradient-to-r from-orange-600 to-red-600 text-white rounded-xl font-black text-[10px] uppercase tracking-widest flex items-center justify-center gap-1 hover:opacity-90 transition-all shadow-sm">
              <Icon icon="mdi:view-list" class="text-base" /> Opciones y Productos
            </button>
            <button @click.stop="openEditModal(mod)"
              class="py-2.5 bg-white dark:bg-gray-800 text-amber-600 border border-gray-200 dark:border-gray-600 rounded-xl font-black text-[10px] flex items-center justify-center gap-1 hover:bg-amber-50 transition-colors">
              <Icon icon="mdi:pencil" class="text-base" /><span>Editar</span>
            </button>
            <button @click.stop="abrirModalToggleStatus(mod)"
              class="py-2.5 bg-white dark:bg-gray-800 text-indigo-600 border border-gray-200 dark:border-gray-600 rounded-xl font-black text-[10px] flex items-center justify-center gap-1 hover:bg-indigo-50 transition-colors">
              <Icon :icon="mod.estado === 'activo' ? 'mdi:eye-off' : 'mdi:eye'" class="text-base" />
              <span>{{ mod.estado === 'activo' ? 'Desact.' : 'Activar' }}</span>
            </button>
            <button @click.stop="abrirModalEliminar(mod)"
              class="py-2.5 bg-white dark:bg-gray-800 text-rose-600 border border-gray-200 dark:border-gray-600 rounded-xl font-black text-[10px] flex items-center justify-center gap-1 hover:bg-rose-50 transition-colors">
              <Icon icon="mdi:delete" class="text-base" /><span>Eliminar</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- MODAL FORM -->
    <Teleport to="body">
      <div v-if="showModal"
        class="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9998] flex items-end sm:items-center justify-center sm:p-4"
        @click.self="closeModal">
        <div class="bg-white dark:bg-gray-800 w-full max-w-lg sm:rounded-2xl rounded-t-3xl shadow-2xl overflow-hidden animate-slideUp sm:animate-zoomIn flex flex-col max-h-[90vh]">
          <div class="bg-gradient-to-r from-orange-600 to-red-600 px-5 py-4 flex items-center gap-3 shrink-0">
            <div class="p-2 bg-white/20 rounded-xl">
              <Icon :icon="editingMod ? 'mdi:pencil-box' : 'mdi:tune-variant'" class="text-white text-xl" />
            </div>
            <div>
              <h3 class="text-base font-black text-white">{{ editingMod ? 'Editar Modificador' : 'Nuevo Modificador' }}</h3>
              <p class="text-[11px] text-white/80 font-bold">{{ editingMod ? 'Modifica los datos' : 'Completa los datos' }}</p>
            </div>
            <button @click="closeModal" class="ml-auto p-2 bg-white/10 rounded-xl">
              <Icon icon="mdi:close" class="text-white text-xl" />
            </button>
          </div>
          <div class="p-5 overflow-y-auto custom-scrollbar">
            <form @submit.prevent="saveModificador" class="space-y-4">
              <div class="bg-gray-50 dark:bg-gray-900/30 rounded-2xl p-4 border border-gray-100 dark:border-gray-700">
                <label class="block text-[10px] font-black text-gray-400 uppercase mb-2 tracking-widest">Nombre *</label>
                <input v-model="form.nombre" required maxlength="100"
                  class="w-full px-4 py-3 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 focus:border-orange-500 rounded-xl text-sm font-bold text-gray-800 dark:text-white outline-none"
                  placeholder="Ej: Término de la carne, Salsas extra" />
              </div>

              <div class="bg-gray-50 dark:bg-gray-900/30 rounded-2xl p-4 border border-gray-100 dark:border-gray-700">
                <label class="block text-[10px] font-black text-gray-400 uppercase mb-2 tracking-widest">Tipo</label>
                <select v-model="form.tipo"
                  class="w-full px-4 py-3 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 focus:border-orange-500 rounded-xl text-sm font-bold text-gray-800 dark:text-white outline-none">
                  <option value="opcion">Opción</option>
                  <option value="ingrediente">Ingrediente</option>
                  <option value="salsa">Salsa</option>
                  <option value="extra">Extra</option>
                </select>
              </div>

              <div class="bg-gray-50 dark:bg-gray-900/30 rounded-2xl p-4 border border-gray-100 dark:border-gray-700 space-y-3">
                <label class="flex items-center gap-3 cursor-pointer">
                  <input type="checkbox" v-model="form.es_obligatorio" class="w-5 h-5 rounded text-red-500" />
                  <div>
                    <span class="text-sm font-black text-gray-700 dark:text-gray-200">Obligatorio</span>
                    <p class="text-[10px] text-gray-400 font-medium">El cliente debe elegir una opción</p>
                  </div>
                </label>
                <label class="flex items-center gap-3 cursor-pointer">
                  <input type="checkbox" v-model="form.seleccion_multiple" class="w-5 h-5 rounded text-blue-500" />
                  <div>
                    <span class="text-sm font-black text-gray-700 dark:text-gray-200">Selección múltiple</span>
                    <p class="text-[10px] text-gray-400 font-medium">Puede elegir varias opciones</p>
                  </div>
                </label>
                <div v-if="form.seleccion_multiple" class="bg-white dark:bg-gray-800 rounded-xl p-3 border border-gray-200 dark:border-gray-600">
                  <label class="block text-[10px] font-black text-gray-400 uppercase mb-1">Máx. selecciones</label>
                  <input v-model.number="form.max_selecciones" type="number" min="1" max="20"
                    class="w-full px-3 py-2 border-2 border-gray-200 dark:border-gray-600 rounded-lg text-sm font-black text-center" />
                </div>
              </div>

              <div class="bg-gray-50 dark:bg-gray-900/30 rounded-2xl p-4 border border-gray-100 dark:border-gray-700">
                <label class="block text-[10px] font-black text-gray-400 uppercase mb-3 tracking-widest">Estado</label>
                <div class="grid grid-cols-2 gap-3">
                  <label :class="['flex items-center justify-center gap-2 py-3 rounded-xl border-2 font-black text-xs cursor-pointer',
                    form.estado === 'activo' ? 'bg-emerald-50 border-emerald-500 text-emerald-700' : 'bg-white border-gray-200 text-gray-500']">
                    <input type="radio" v-model="form.estado" value="activo" class="hidden" /> Activo
                  </label>
                  <label :class="['flex items-center justify-center gap-2 py-3 rounded-xl border-2 font-black text-xs cursor-pointer',
                    form.estado === 'inactivo' ? 'bg-rose-50 border-rose-500 text-rose-700' : 'bg-white border-gray-200 text-gray-500']">
                    <input type="radio" v-model="form.estado" value="inactivo" class="hidden" /> Inactivo
                  </label>
                </div>
              </div>

              <div class="flex flex-col-reverse sm:flex-row gap-3 pt-4 border-t border-gray-100 dark:border-gray-700">
                <button type="button" @click="closeModal"
                  class="w-full sm:w-auto px-6 py-3 border-2 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 rounded-xl font-black text-xs uppercase tracking-widest">
                  Cancelar
                </button>
                <button type="submit" :disabled="saving"
                  class="w-full flex-1 py-3 bg-gradient-to-r from-orange-600 to-red-600 text-white rounded-xl font-black text-xs uppercase tracking-widest disabled:opacity-50 flex items-center justify-center gap-2 shadow-lg">
                  <Icon :icon="saving ? 'mdi:loading' : 'mdi:content-save'" :class="{'animate-spin': saving}" class="text-lg" />
                  {{ saving ? 'Guardando...' : (editingMod ? 'Actualizar' : 'Crear') }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- MODAL CONFIRMACIÓN -->
    <Teleport to="body">
      <div v-if="showConfirmModal"
        class="fixed inset-0 bg-black/60 backdrop-blur-sm z-[10002] flex items-end sm:items-center justify-center sm:p-4"
        @click.self="cancelarConfirmacion">
        <div class="bg-white dark:bg-gray-800 w-full max-w-md sm:rounded-2xl rounded-t-3xl shadow-2xl overflow-hidden animate-slideUp sm:animate-zoomIn">
          <div class="px-5 py-4" :class="confirmModalColor">
            <div class="flex items-center gap-3">
              <div class="p-2 bg-white/20 rounded-xl">
                <Icon :icon="confirmModalIcon" class="text-white text-xl" />
              </div>
              <div>
                <h3 class="text-base font-black text-white">{{ confirmModalTitulo }}</h3>
                <p class="text-[11px] text-white/80 font-bold">{{ confirmModalSubtitulo }}</p>
              </div>
            </div>
          </div>
          <div class="p-6 text-center">
            <p class="text-sm font-bold text-gray-700 dark:text-gray-300">{{ confirmModalMensaje }}</p>
            <p v-if="confirmModalDetalle" class="text-xs text-gray-500 mt-2">{{ confirmModalDetalle }}</p>
          </div>
          <div class="px-5 py-4 bg-gray-50 dark:bg-gray-900 border-t border-gray-100 dark:border-gray-700 flex flex-col-reverse sm:flex-row gap-3">
            <button @click="cancelarConfirmacion"
              class="w-full sm:flex-1 py-3 border-2 border-gray-200 dark:border-gray-700 text-gray-600 rounded-xl font-black text-xs uppercase tracking-widest">
              Cancelar
            </button>
            <button @click="ejecutarAccionConfirmada" :disabled="confirmModalCargando"
              class="w-full sm:flex-[2] py-3 rounded-xl font-black text-xs uppercase tracking-widest text-white flex items-center justify-center gap-2 shadow-lg"
              :class="confirmModalButtonColor">
              <Icon :icon="confirmModalCargando ? 'mdi:loading' : 'mdi:check'" :class="{'animate-spin': confirmModalCargando}" class="text-lg" />
              {{ confirmModalCargando ? 'Procesando...' : confirmModalBotonTexto }}
            </button>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
import { Icon } from "@iconify/vue";
import Swal from 'sweetalert2';
import {
  obtenerModificadores, crearModificador, actualizarModificador, eliminarModificador,
  convertirEstadoATexto,
} from '@/api/modificadores.js';

export default {
  name: 'ModificadoresList',
  components: { Icon },
  data() {
    return {
      modificadores: [], loading: false, saving: false, showModal: false,
      searchQuery: '', filterStatus: 'all',
      form: { nombre: '', tipo: 'opcion', es_obligatorio: false, seleccion_multiple: false, max_selecciones: 1, estado: 'activo' },
      editingMod: null,
      showConfirmModal: false, confirmModalTitulo: '', confirmModalSubtitulo: '',
      confirmModalMensaje: '', confirmModalDetalle: '', confirmModalIcon: '',
      confirmModalColor: '', confirmModalButtonColor: '', confirmModalBotonTexto: '',
      confirmModalCargando: false, confirmModalAccion: null, confirmModalDatos: null,
    };
  },
  computed: {
    modificadoresFiltrados() {
      let arr = [...this.modificadores];
      if (this.searchQuery.trim()) {
        const t = this.searchQuery.toLowerCase();
        arr = arr.filter(m => m.nombre?.toLowerCase().includes(t));
      }
      if (this.filterStatus === 'activos') arr = arr.filter(m => m.estado === 'activo');
      else if (this.filterStatus === 'inactivos') arr = arr.filter(m => m.estado === 'inactivo');
      return arr;
    },
  },
  mounted() { this.cargarModificadores(); },
  methods: {
    mostrarToast(icon, title, text = '') {
      Swal.fire({ toast: true, position: 'top-end', icon, title, text, showConfirmButton: false, timer: 3000, timerProgressBar: true });
    },
    async cargarModificadores() {
      this.loading = true;
      try {
        const r = await obtenerModificadores();
        if (r.success) this.modificadores = r.data || [];
      } finally { this.loading = false; }
    },
    irADetalle(mod) {
      this.$router.push({ name: 'ModificadorDetalle', params: { id: mod.idModificador } });
    },
    openCreateModal() {
      this.editingMod = null;
      this.form = { nombre: '', tipo: 'opcion', es_obligatorio: false, seleccion_multiple: false, max_selecciones: 1, estado: 'activo' };
      this.showModal = true;
    },
    openEditModal(m) {
      this.editingMod = m;
      this.form = {
        nombre: m.nombre, tipo: m.tipo,
        es_obligatorio: !!m.es_obligatorio,
        seleccion_multiple: !!m.seleccion_multiple,
        max_selecciones: m.max_selecciones ?? 1,
        estado: m.estado,
      };
      this.showModal = true;
    },
    closeModal() { this.showModal = false; },
    async saveModificador() {
      this.saving = true;
      try {
        const r = this.editingMod
          ? await actualizarModificador(this.editingMod.idModificador, this.form)
          : await crearModificador(this.form);
        if (r.success) {
          await this.cargarModificadores();
          this.closeModal();
          this.mostrarToast('success', '¡Éxito!', `Modificador ${this.editingMod ? 'actualizado' : 'creado'}.`);
        } else {
          this.mostrarToast('error', 'Error', r.error || 'Error al guardar');
        }
      } finally { this.saving = false; }
    },
    abrirModalEliminar(m) {
      this.abrirModalConfirmacion({
        titulo: 'Eliminar Modificador', subtitulo: 'Se desactivará',
        mensaje: `¿Eliminar "${m.nombre}"?`,
        detalle: 'El modificador se marcará como inactivo.',
        icon: 'mdi:alert-circle',
        color: 'bg-gradient-to-r from-red-600 to-pink-600',
        buttonColor: 'bg-gradient-to-r from-red-600 to-pink-600',
        botonTexto: 'Eliminar', datos: m.idModificador,
        accion: (id) => this.deleteMod(id),
      });
    },
    async deleteMod(id) {
      const r = await eliminarModificador(id);
      if (r.success) {
        await this.cargarModificadores();
        this.mostrarToast('success', 'Desactivado', 'Modificador desactivado.');
      } else {
        this.mostrarToast('error', 'Error', r.error || 'Error');
      }
    },
    abrirModalToggleStatus(m) {
      const esActivo = m.estado === 'activo';
      this.abrirModalConfirmacion({
        titulo: esActivo ? 'Desactivar' : 'Activar',
        subtitulo: 'Cambiar estado',
        mensaje: `¿${esActivo ? 'desactivar' : 'activar'} "${m.nombre}"?`,
        detalle: `Pasará a ${esActivo ? 'inactivo' : 'activo'}.`,
        icon: esActivo ? 'mdi:close-circle' : 'mdi:check-circle',
        color: esActivo ? 'bg-gradient-to-r from-orange-500 to-amber-600' : 'bg-gradient-to-r from-green-600 to-emerald-600',
        buttonColor: esActivo ? 'bg-gradient-to-r from-orange-500 to-amber-600' : 'bg-gradient-to-r from-green-600 to-emerald-600',
        botonTexto: esActivo ? 'Desactivar' : 'Activar',
        datos: m, accion: (mod) => this.toggleStatus(mod),
      });
    },
    async toggleStatus(m) {
      const esActivo = m.estado === 'activo';
      const r = await actualizarModificador(m.idModificador, { ...m, estado: esActivo ? 'inactivo' : 'activo' });
      if (r.success) {
        await this.cargarModificadores();
        this.mostrarToast('success', 'Actualizado', `Modificador ${esActivo ? 'desactivado' : 'activado'}.`);
      } else {
        this.mostrarToast('error', 'Error', r.error || 'Error');
      }
    },
    abrirModalConfirmacion(config) {
      this.confirmModalTitulo = config.titulo || 'Confirmar';
      this.confirmModalSubtitulo = config.subtitulo || '';
      this.confirmModalMensaje = config.mensaje || '¿Estás seguro?';
      this.confirmModalDetalle = config.detalle || '';
      this.confirmModalIcon = config.icon || 'mdi:help-circle';
      this.confirmModalColor = config.color || 'bg-gradient-to-r from-orange-600 to-red-600';
      this.confirmModalButtonColor = config.buttonColor || 'bg-gradient-to-r from-orange-600 to-red-600';
      this.confirmModalBotonTexto = config.botonTexto || 'Confirmar';
      this.confirmModalAccion = config.accion;
      this.confirmModalDatos = config.datos || null;
      this.showConfirmModal = true;
    },
    cancelarConfirmacion() {
      this.showConfirmModal = false;
      this.confirmModalCargando = false;
      this.confirmModalAccion = null;
    },
    async ejecutarAccionConfirmada() {
      if (this.confirmModalAccion) {
        this.confirmModalCargando = true;
        await this.confirmModalAccion(this.confirmModalDatos);
        this.confirmModalCargando = false;
        this.cancelarConfirmacion();
      }
    },
    convertirEstadoATexto,
  },
};
</script>

<style scoped>
.text-lexend { font-family: 'Lexend', sans-serif; }
.custom-scrollbar::-webkit-scrollbar { height: 6px; width: 6px; }
.custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
.dark .custom-scrollbar::-webkit-scrollbar-thumb { background: #475569; }
@keyframes zoomIn { from { opacity: 0; transform: scale(0.95) translateY(10px); } to { opacity: 1; transform: scale(1) translateY(0); } }
.animate-zoomIn { animation: zoomIn 0.25s cubic-bezier(0.16, 1, 0.3, 1); }
@keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
.animate-slideUp { animation: slideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1); }
@keyframes fadeInUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
.animate-fadeInUp { animation: fadeInUp 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
.delay-100 { animation-delay: 0.1s; }
@keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
.animate-fadeIn { animation: fadeIn 0.4s ease-out forwards; }
@media (max-width: 768px) { button { min-height: 44px; } input, select, textarea { font-size: 16px !important; } }
</style>